import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { quillEditorStandardProps, quillFormItemProps } from "@/utils/componentUtils";
import { ibanNormalizeFunction, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { ALL_WHITE_SPACES_PATTERN } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Col, Divider, Form, Input, Row } from "antd";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useGetAgentEnumerations } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { UUID } from "../../../../../typings/global";
import {
  useCurrentAgentAccount,
  useHasAnyPermission,
  useHasPermissions,
  useIsCurrentAgentTypePlus,
} from "../../../../auth/hooks";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import CommercialRegisterDistrictOfficeAutoComplete from "../../../../enumerations/components/form/CommercialRegisterDistrictOfficeAutoComplete";
import CommercialRegisterSectionAutoComplete from "../../../../enumerations/components/form/CommercialRegisterSectionAutoComplete";

interface Props {
  colSpan: number;
  disableCrnInput?: boolean;
  searchInProgress?: boolean;
  onCrnSearchOrChange?: (crn: string) => void;
  clientManagedById?: UUID;
  isCreatingNew?: boolean;
}

export const LegalClientDataFormPart = ({
  colSpan,
  disableCrnInput,
  searchInProgress,
  onCrnSearchOrChange,
  clientManagedById,
  isCreatingNew,
}: Props) => {
  const { t } = useTranslation();

  const currentAgent = useCurrentAgentAccount()?.agent;
  const hasManagedByChangePermission = useHasAnyPermission(
    Permission.CLIENT_MANAGED_BY_CHANGE,
    Permission.PRIVILEGED_CLIENT_MANAGED_BY_CHANGE,
  );
  const hasPrivilegedManagedByChangePermission = useHasPermissions(Permission.PRIVILEGED_CLIENT_MANAGED_BY_CHANGE);
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data: defaultAgent } = useGetAgentEnumerations(clientManagedById ? [clientManagedById] : []);

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="managedById"
            label={
              <LabelWithTooltip tooltip={t("client.helpers.managedByHint")} label={t("client.attrs.managedById")} />
            }
            rules={[validations.notNull]}
            initialValue={isTopAgentPlus ? undefined : currentAgent?.id}
          >
            <AgentSelect
              optionsProps={{
                subordinateTreePath:
                  isCreatingNew || hasPrivilegedManagedByChangePermission ? undefined : currentAgent?.accessTreePath,
                defaultOption: defaultAgent?.[0],
              }}
              disabled={!isCreatingNew && !hasManagedByChangePermission}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyRegistrationNumber"
            label={t("client.attrs.companyRegistrationNumber")}
            rules={[validations.notBlank, validations.crn]}
          >
            {onCrnSearchOrChange ? (
              <Input.Search
                disabled={disableCrnInput}
                loading={searchInProgress}
                onSearch={onCrnSearchOrChange}
                onChange={(e) => onCrnSearchOrChange?.(e.target.value)}
              />
            ) : (
              <Input disabled={disableCrnInput} />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyName"
            label={t("client.attrs.companyName")}
            rules={[validations.notBlank, validations.size(1, 255)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <CommercialRegisterDistrictOfficeAutoComplete
            formItemProps={{
              name: "commercialRegisterDistrictOffice",
              label: t("client.attrs.commercialRegisterDistrictOffice"),
              rules: [validations.size(1, 64)],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="taxId" label={t("client.attrs.taxId")} rules={[validations.length(10), validations.numeric]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <CommercialRegisterSectionAutoComplete
            formItemProps={{
              name: "commercialRegisterSection",
              label: t("client.attrs.commercialRegisterSection"),
              rules: [validations.size(1, 64)],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="vatId"
            label={<LabelWithTooltip label={t("client.attrs.vatId")} tooltip={t("agent.helpers.vatPayerHint")} />}
            rules={[validations.length(12), validations.pattern(regexPatterns.vatIdRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="commercialRegisterInsertionNumber"
            label={t("client.attrs.commercialRegisterInsertionNumber")}
            rules={[validations.size(1, 64)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="bankAccountNumber"
            label={t("client.attrs.bankAccountNumber")}
            rules={[validations.size(1, 34), validations.iban]}
            getValueProps={(value) => ({ value: value ? ibanNormalizeFunction(value) : value })}
            normalize={(value) => value?.replace(ALL_WHITE_SPACES_PATTERN, "")}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="bankSwift"
            label={t("client.attrs.bankSwift")}
            rules={[validations.size(4, 8)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Divider titlePlacement="start">{t("client.titles.additionalData")}</Divider>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 4}>
          <Form.Item
            name="note"
            label={t("client.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
